/**
 * Files attached to one candidate or one employee.
 *
 * The same panel serves both, because the difference that matters is enforced
 * on the server: candidate documents follow candidate visibility, employee
 * documents are approver-level. Duplicating that rule here would only create a
 * second place for it to drift.
 *
 * The accepted-types line is shown before anyone picks a file rather than as an
 * error afterwards. Being told "PDF, JPEG, PNG, DOCX or XLSX" up front is worth
 * more than being told "not an accepted file type" once the upload has failed.
 */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Loader2, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { client } from '../api/client';
import type { DocumentType, Page, StoredDocument } from '../types';

/**
 * How every stored type reads. Exhaustive on purpose: a new member added to
 * the enum fails the typecheck here rather than rendering as a raw string.
 */
const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  resume: 'Resume',
  id_proof: 'ID proof',
  address_proof: 'Address proof',
  education_certificate: 'Education certificate',
  experience_letter: 'Experience letter',
  offer_letter: 'Offer letter',
  bank_proof: 'Bank proof',
  profile_photo: 'Passport photo',
  aadhaar_front: 'Aadhaar (front)',
  aadhaar_back: 'Aadhaar (back)',
  pan_card: 'PAN card',
  other: 'Other',
};

/**
 * What the Type dropdown offers, which is not everything the model knows.
 *
 * Labelling a type and offering it are two different things. Aadhaar and PAN
 * have labels so that a row of that type would render properly if one ever
 * existed, but they are not on this list: encryption at rest is unfinished
 * (HARDENING.md names it), and an Aadhaar image is the number itself in a
 * form no redaction touches. Offering the upload before that lands would be
 * inviting somebody to store it.
 *
 * Not a security control. The endpoint still accepts every member of the
 * enum and validates exactly as before, so this decides what is offered
 * rather than what is permitted — the same distinction `allowedTypes` below
 * already draws. The real gate is that nothing has been built to encrypt it.
 */
const UPLOADABLE: DocumentType[] = [
  'resume',
  'id_proof',
  'address_proof',
  'education_certificate',
  'experience_letter',
  'offer_letter',
  'bank_proof',
  'profile_photo',
  'aadhaar_front',
  'aadhaar_back',
  'pan_card',
  'other',
];

/** Mirrors the server allowlist. SVG is absent there and must stay absent here. */
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.docx,.xlsx';

/**
 * A resume is a document, so the picker only offers documents.
 *
 * The client asked for Word or PDF and the reason is practical: a resume
 * arrives on WhatsApp as a photo of a printout often enough that the picker
 * defaulting to images gets one uploaded, and a JPEG cannot be read by
 * anything downstream -- not the profile PDF, not a client's own system, not a
 * search. Narrowing the dialog is what stops it arriving in the first place.
 *
 * `.doc` is deliberately absent: the server's allowlist carries `.docx` and
 * not the old binary format, so offering it here would produce a file dialog
 * that accepts something the upload then refuses.
 *
 * An affordance, not a rule -- the same distinction `allowedTypes` draws. The
 * endpoint still validates every upload exactly as before, and a caller
 * reading this as a guarantee would be wrong.
 */
const ACCEPT_DOCUMENT = '.pdf,.docx';

function readableSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} bytes`;
}

/**
 * Read the message out of an error whose body is a Blob.
 *
 * Downloads use responseType 'blob', so even a 404 arrives as binary and the
 * ordinary error helper cannot see the detail inside it.
 */
async function messageFrom(error: unknown, fallback: string): Promise<string> {
  const body = (error as { response?: { data?: unknown } })?.response?.data;
  if (body instanceof Blob) {
    try {
      const parsed = JSON.parse(await body.text()) as { detail?: unknown };
      if (typeof parsed.detail === 'string') return parsed.detail;
    } catch {
      // Not JSON; fall through.
    }
  }
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response
    ?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

export default function DocumentsPanel({
  title,
  candidateId,
  employeeId,
  allowedTypes,
  onClose,
}: {
  title: string;
  candidateId?: string;
  employeeId?: string;
  /**
   * Narrows what the Type dropdown offers. Omitted everywhere but the
   * Interview Candidates list, which only wants resumes.
   *
   * An affordance, not a rule. The upload endpoint still accepts every type
   * and validates exactly as before — this decides what is offered, not what
   * is permitted, and a caller reading it as a guarantee would be wrong.
   */
  allowedTypes?: DocumentType[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const offered = allowedTypes?.length ? allowedTypes : UPLOADABLE;
  // The existing default unless it is not on offer, in which case the first
  // thing that is — a select whose value is absent from its options renders
  // blank and posts whatever the user never chose.
  const initial: DocumentType = candidateId ? 'resume' : 'id_proof';
  const [docType, setDocType] = useState<DocumentType>(
    offered.includes(initial) ? initial : offered[0],
  );
  /**
   * The chosen file, held until Add is pressed.
   *
   * The input used to upload straight from onChange, which made picking a file
   * an irreversible act and — worse — read the Type as it stood at that
   * instant. Choosing the file and then correcting the Type sent the old one,
   * with nothing on screen to say so.
   */
  const [file, setFile] = useState<File | null>(null);

  const owner = candidateId ? { candidateId } : { employeeId };
  const key = ['documents', candidateId ?? employeeId];

  const documents = useQuery<Page<StoredDocument>>({
    queryKey: key,
    queryFn: async () =>
      (await client.get('/api/v1/documents', { params: { ...owner, limit: 200 } })).data,
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      // Snake_case by hand: the request interceptor skips FormData, so the
      // camelCase conversion every other call relies on does not happen here.
      form.append('doc_type', docType);
      if (candidateId) form.append('candidate_id', candidateId);
      if (employeeId) form.append('employee_id', employeeId);
      return (await client.post('/api/v1/documents', form)).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
      if (fileInput.current) fileInput.current.value = '';
      setFile(null);
      toast.success('File uploaded');
    },
    onError: (error) => {
      void messageFrom(error, 'Could not upload that file.').then((m) => toast.error(m));
    },
  });

  const download = useMutation({
    mutationFn: async (doc: StoredDocument) => {
      const response = await client.get(`/api/v1/documents/${doc.id}/download`, {
        responseType: 'blob',
      });
      return { doc, blob: response.data as Blob };
    },
    onSuccess: ({ doc, blob }) => {
      // Fetched rather than linked: the endpoint needs the auth header.
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = doc.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    },
    onError: (error) => {
      void messageFrom(error, 'Could not download that file.').then((m) =>
        toast.error(m),
      );
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) =>
      (await client.delete(`/api/v1/documents/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
      toast.success('File deleted');
    },
    onError: (error) => {
      void messageFrom(error, 'Could not delete that file.').then((m) => toast.error(m));
    },
  });

  const rows = documents.data?.items ?? [];

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl">
        {/* No cross in the corner. Uploading is the point of this panel and it
            has no save step -- each file is sent when Add is pressed -- so the
            only thing left to do is say you are finished, and a labelled
            button says that better than a glyph. */}
        <div className="border-b border-gray-200 px-5 py-4">
          <h3 className="text-sm font-semibold text-gray-900">Files — {title}</h3>
          <p className="mt-1 text-xs text-gray-500">
            PDF, JPEG, PNG, DOCX or XLSX, up to 25 MB. Downloads are recorded.
          </p>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">

        <div className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-gray-50 p-3">
          <label className="text-xs text-gray-600">
            Type
            <select
              className="input mt-1"
              value={docType}
              onChange={(e) => setDocType(e.target.value as DocumentType)}
            >
              {offered.map((key_) => (
                <option key={key_} value={key_}>
                  {DOC_TYPE_LABELS[key_]}
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs text-gray-600">
            File
            <input
              ref={fileInput}
              className="input mt-1 text-xs"
              type="file"
              accept={docType === 'resume' ? ACCEPT_DOCUMENT : ACCEPT}
              disabled={upload.isPending}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>

          {/* Nothing is sent until this is pressed, so the Type beside it can
              still be corrected after the file is chosen. */}
          <button
            type="button"
            className="btn-primary"
            disabled={!file || upload.isPending}
            onClick={() => file && upload.mutate(file)}
          >
            {upload.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Add
          </button>
        </div>

        {documents.isLoading ? (
          <div className="flex justify-center py-8 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : documents.isError ? (
          <p className="py-4 text-sm text-red-700">Could not load files.</p>
        ) : rows.length === 0 ? (
          <p className="flex items-center gap-2 py-6 text-sm text-gray-500">
            <FileText className="h-4 w-4 text-gray-400" />
            Nothing attached yet.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-400">
                <th className="pb-2 font-medium">File</th>
                <th className="pb-2 font-medium">Type</th>
                <th className="pb-2 font-medium">Size</th>
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((doc) => (
                <tr key={doc.id}>
                  <td className="max-w-xs truncate py-2 text-gray-900" title={doc.filename}>
                    {doc.filename}
                  </td>
                  <td className="py-2 text-gray-500">{DOC_TYPE_LABELS[doc.docType]}</td>
                  <td className="py-2 text-gray-500">{readableSize(doc.sizeBytes)}</td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      className="btn-ghost text-xs"
                      disabled={download.isPending}
                      onClick={() => download.mutate(doc)}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      className="btn-ghost text-xs text-red-600"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(doc.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </div>

        {/* Outside the scroll, so it stays put however many files are listed. */}
        <div className="flex justify-end border-t border-gray-200 px-5 py-3">
          <button type="button" className="btn-primary" onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
